import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import {
  Badge,
  Box,
  Link,
  Typography,
  IconButton,
  Button,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import useVerticals from 'data_sources/useVerticals';
import useJurisdictions from 'data_sources/useJurisdictions';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import datetimeHelper from 'helpers/datetimeHelper';
import Modal from 'components/_layouts/Modal';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import parse from 'html-react-parser';
import errorHelper from 'helpers/errorHelper';
import useAccreditationBody from '../../../_data_sources/AccreditationBodies/useAccreditationBody';
import useAccreditationVersions from '../../../_data_sources/AccreditationVersions/useAccreditationVersions';
import { AccreditationVersion, JurisdictionWithPublishedState } from '../../../_types/awb';
import { publishAccreditationVersion } from '../../../_data_sources/AccreditationVersions/manageAccreditationVersions';

type ExpandedAccreditationVersion = AccreditationVersion & {
  isMainRow?: boolean;
  isExpanded?: boolean;
  parentId?: string;
  currentJurisdiction?: any;
  jurisdictionIndex?: number;
};
import useUrlParams from 'hooks/useUrlParams';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import NewAccreditingBodyVersion from '../New';
import EditAccreditingBodyVersion from '../Edit';
import CloneVersion from '../Clone';
import { ServerErrorResponse } from 'data_sources/constants';
import ReactSelect from 'react-select';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import VerticalPicker from 'components/_pickers/VerticalPicker';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PhxBadge from '_lex/_atoms/PhxBadge';

type AccreditingBodyVersionsListParams = {
  name?: string | null;
  publishedState?: 'PUBLISHED' | 'DRAFT' | '';
  jurisdiction?: string | null;
  vertical?: string | null;
};

const defaultParams: AccreditingBodyVersionsListParams = {
  name: null,
  jurisdiction: null,
  publishedState: '',
  vertical: null,
};

const AccreditationVersionsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: accreditationBodyId } = useParams();
  const verticalQuery = useVerticals();
  const [params, setParams] = useUrlParams(defaultParams);
  const [accreditationVersions, setAccreditationVersions] = useState<ExpandedAccreditationVersion[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [lastId, setLastId] = useState<string | null>(null); // Track the last item ID created or updated
  const [editId, setEditId] = useState<string | null>(null);
  const [cloneId, setCloneId] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedVersionForRelease, setSelectedVersionForRelease] = useState<ExpandedAccreditationVersion | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notificationMode, setNotificationMode] = useState<'silent' | 'notify'>('silent');
  const bodyQuery = useAccreditationBody(accreditationBodyId ?? '');

  const updateParams = useCallback(
    (updatedParams: AccreditingBodyVersionsListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams = { ...params };

    return _.omitBy(newParams, _.isEmpty);
  }, [params]);

  const gridViewQuery = useAccreditationVersions<AccreditationVersion>(
    accreditationBodyId ?? '',
    queryParams,
    (errors: ServerErrorResponse) => {
      console.error('Error fetching accreditation versions:', errors);
    }
  );

  // Function to expand rows with multiple jurisdictions
  const expandRowsWithJurisdictions = (versions: AccreditationVersion[]) => {
    const expandedData: ExpandedAccreditationVersion[] = [];

    versions.forEach((version) => {
      if (!version.id) return;

      const versionId = version.id;
      const isExpanded = expandedRows.has(versionId);

      if (version.jurisdictions.length > 1 && isExpanded) {
        // Add the main row - stays the same as when not expanded (showing all jurisdictions)
        expandedData.push({
          ...version,
          isMainRow: true,
          parentId: versionId,
          jurisdictionIndex: 0,
          currentJurisdiction: null, // No specific jurisdiction - shows all like when collapsed
          isExpanded: true,
        });

        // Add additional rows for each jurisdiction (one per jurisdiction)
        for (let i = 0; i < version.jurisdictions.length; i++) {
          expandedData.push({
            ...version,
            id: `${versionId}_jurisdiction_${i}`,
            isMainRow: false,
            parentId: versionId,
            jurisdictionIndex: i,
            currentJurisdiction: version.jurisdictions[i],
            isExpanded: true,
          });
        }
      } else {
        // Add the regular row (not expanded or single jurisdiction)
        expandedData.push({
          ...version,
          isMainRow: true,
          parentId: versionId,
          jurisdictionIndex: 0,
          currentJurisdiction: version.jurisdictions.length === 1 ? version.jurisdictions[0] : null,
          isExpanded: false,
        });
      }
    });

    return expandedData;
  };

  useEffect(() => {
    if (gridViewQuery.data && !gridViewQuery.isError) {
      const fetchedAccreditationVersions = gridViewQuery.data.pages.flatMap((page) => page.content) || [];
      const expandedData = expandRowsWithJurisdictions(fetchedAccreditationVersions);
      setAccreditationVersions(expandedData);
    }
  }, [gridViewQuery.data, gridViewQuery.isError, verticalQuery.data, expandedRows]);

  const handleRelease = (row: ExpandedAccreditationVersion) => {
    const jurisdictions = row.jurisdictions as JurisdictionWithPublishedState[];

    if (jurisdictions.length > 1) {
      navigate(`./${row.id}/release`);
      return;
    }

    const singleJurisdiction = jurisdictions[0];

    if (singleJurisdiction.publishedState !== 'DRAFT') {
      setSelectedVersionForRelease(row);
      setShowConfirmModal(true);
    } else {
      performRelease(row, [singleJurisdiction.id]);
    }
  };

  const performRelease = (row: ExpandedAccreditationVersion, jurisdictionIds: string[]) => {
    setIsSaving(true);

    const publishData = {
      jurisdictions: jurisdictionIds,
      republishNotificationRequired: notificationMode === 'notify',
    };

    publishAccreditationVersion(
      accreditationBodyId ?? '',
      row.id ?? '',
      publishData,
      () => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setSelectedVersionForRelease(null);

        enqueueSnackbar(
          parse(
            t('awb.accreditingBodyVersions.release.successToast', '', {
              versionName: row.version || '',
              jurisdictionCount: jurisdictionIds.length.toString(),
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );

        queryClient.invalidateQueries(['accreditation_versions', accreditationBodyId]);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setShowConfirmModal(false);
        setSelectedVersionForRelease(null);
        console.error('Release error:', errors);
        errorHelper.enqueueErrors(errors);
      }
    );
  };

  const handleConfirmRelease = () => {
    if (!selectedVersionForRelease) return;

    const jurisdictions = selectedVersionForRelease.jurisdictions as JurisdictionWithPublishedState[];
    const jurisdictionIds = jurisdictions.map((j) => j.id);

    performRelease(selectedVersionForRelease, jurisdictionIds);
  };

  const lookupVertical = useCallback(
    (verticalId: string | undefined) => {
      return verticalId && verticalQuery.data
        ? verticalQuery.data.find((vertical) => vertical.id === verticalId)
        : null;
    },
    [verticalQuery.data, gridViewQuery.data]
  );

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const columns: ColumnDef = useMemo(
    () => ({
      abbreviation: {
        header: t('awb.accreditingBodyVersions.properties.abbreviation'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row || !row.isMainRow) return null;

          const originalVersion = row;
          const hasMultipleJurisdictions = originalVersion.jurisdictions.length > 1;
          const isExpanded = row.parentId ? expandedRows.has(row.parentId) : false;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasMultipleJurisdictions && (
                <IconButton
                  size='small'
                  onClick={() => {
                    if (row.parentId) {
                      toggleRowExpansion(row.parentId);
                    }
                  }}
                  sx={{
                    p: 0.25,
                    minWidth: 'auto',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem',
                    },
                  }}
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
              <Link
                id={`accrediting_body_${row.parentId}`}
                href={`./${row.parentId}/standards`}
              >
                {row.abbreviation}
              </Link>
            </Box>
          );
        },
      },
      version: {
        header: t('awb.accreditingBodyVersions.properties.version'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row || !row.isMainRow) return null;
          return <span>{row.version}</span>;
        },
      },
      jurisdictions: {
        header: t('awb.accreditingBodyVersions.properties.jurisdictions'),
        flex: 0.8,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row) return null;

          // If it's an expanded sub-row, show only the current jurisdiction
          if (row.isExpanded && row.currentJurisdiction && !row.isMainRow) {
            const jurisdictionName =
              typeof row.currentJurisdiction === 'string' ? row.currentJurisdiction : row.currentJurisdiction.name;
            return <span>{jurisdictionName}</span>;
          }

          // If it's the main row (expanded or not), show all jurisdictions together
          if (row.isMainRow && row.jurisdictions) {
            const allJurisdictions = row.jurisdictions
              .map((jurisdiction: any) => (typeof jurisdiction === 'string' ? jurisdiction : jurisdiction.abbreviation))
              .join(', ');
            return <span>{allJurisdictions}</span>;
          }

          // Handle single jurisdiction case
          if (!row.isExpanded && row.currentJurisdiction) {
            const jurisdictionName =
              typeof row.currentJurisdiction === 'string'
                ? row.currentJurisdiction
                : row.currentJurisdiction.abbreviation;
            return <span>{jurisdictionName}</span>;
          }

          return <span>-</span>;
        },
        formatter: (value: any[]) => {
          return value
            .map((jurisdiction) => (typeof jurisdiction === 'string' ? jurisdiction : jurisdiction.abbreviation))
            .join(', ');
        },
      },
      verticalId: {
        header: t('awb.accreditingBodyVersions.properties.vertical'),
        flex: 0.8,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row || !row.isMainRow) return null;
          const ids = row.verticals?.length ? row.verticals : row.verticalId ? [row.verticalId] : [];
          const names = ids.map((id) => lookupVertical(id)?.name).filter(Boolean) as string[];
          const maxVisible = 1;
          const visible = names.slice(0, maxVisible);
          const rest = names.slice(maxVisible);
          const restCount = rest.length;
          return (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {visible.length > 0 && <span>{visible.join(', ')}</span>}
              {restCount > 0 && (
                <Tooltip title={rest.join(', ')} placement="top-start">
                  <span>
                    <PhxBadge color="neutral" size="sm">
                      +{restCount}
                    </PhxBadge>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
        formatter: (value: string | null | undefined, row?: ExpandedAccreditationVersion) => {
          if (row?.verticals?.length) {
            return row.verticals.map((id) => lookupVertical(id)?.name).filter(Boolean).join(', ');
          }
          return value ? lookupVertical(value)?.name ?? null : null;
        },
      },
      createdAt: {
        header: t('awb.accreditingBodyVersions.properties.createdAt'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row || !row.isMainRow) return null;
          return <span>{datetimeHelper.formatDate(row.createdAt)}</span>;
        },
      },
      updatedAt: {
        header: t('awb.accreditingBodyVersions.properties.updatedAt'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row) return null;

          // For expanded sub-rows, use jurisdiction-specific lastPublishedAt
          if (row.isExpanded && !row.isMainRow && row.currentJurisdiction) {
            const jurisdiction = row.currentJurisdiction as JurisdictionWithPublishedState;
            return <span>{datetimeHelper.formatDate(jurisdiction.lastPublishedAt)}</span>;
          }

          // For main rows, use the version's updatedAt
          if (row.isMainRow) {
            return <span>{datetimeHelper.formatDate(row.updatedAt)}</span>;
          }

          return null;
        },
      },
      publishedState: {
        header: t('awb.accreditingBodyVersions.properties.publishedState'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row) return null;

          // For expanded sub-rows, use jurisdiction-specific publishedState
          if (row.isExpanded && !row.isMainRow && row.currentJurisdiction) {
            const jurisdiction = row.currentJurisdiction as JurisdictionWithPublishedState;
            return (
              <span>
                <PhxBadge color={jurisdiction.publishedState === 'PUBLISHED' ? 'success' : 'neutral'}>
                  {t(`awb.accreditingBodyVersions.publishedState.${jurisdiction.publishedState}`)}
                </PhxBadge>
              </span>
            );
          }

          // For main rows with multiple jurisdictions, show count
          if (row.isMainRow && row.jurisdictions && row.jurisdictions.length > 1) {
            // Type guard to ensure jurisdictions is an array of objects, not strings
            const jurisdictions = row.jurisdictions as JurisdictionWithPublishedState[];
            const publishedCount = jurisdictions.filter(
              (jurisdiction) => jurisdiction.publishedState === 'PUBLISHED'
            ).length;
            const totalCount = jurisdictions.length;

            return (
              <span>
                {publishedCount} of {totalCount} released
              </span>
            );
          }

          // For main rows with single jurisdiction or other cases, use version's publishedState
          return (
            <span>
              {
                <PhxBadge color={row.publishedState === 'PUBLISHED' ? 'success' : 'neutral'}>
                  {t(`awb.accreditingBodyVersions.publishedState.${row.publishedState}`)}
                </PhxBadge>
              }
            </span>
          );
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.2,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedAccreditationVersion;
          if (!row || !row.isMainRow || !row.parentId) return null;
          const parentId = row.parentId;
          const items: ActionMenuItemProps[] = [
            {
              key: `open_${parentId}`,
              label: t('awb.accreditingBodyVersions.actions.open'),
              onClick: () => {
                navigate(`./${parentId}/standards`);
              },
            },
            {
              key: `edit_${parentId}`,
              label: t('awb.accreditingBodyVersions.actions.edit'),
              onClick: () => setEditId(parentId),
            },
            {
              key: `clone_${parentId}`,
              label: t('awb.accreditingBodyVersions.actions.clone'),
              onClick: () => setCloneId(parentId),
            },
            {
              key: `release_${row.id}`,
              label: t('awb.accreditingBodyVersions.actions.release'),
              onClick: () => handleRelease(row),
            },
          ];
          return (
            <ActionMenu
              id={`action_menu_${parentId}`}
              items={items}
            />
          );
        },
      },
    }),
    [t, lookupVertical, expandedRows]
  );

  const actions = useMemo(() => {
    if (bodyQuery.data) {
      return (
        <NewAccreditingBodyVersion
          accreditingBody={bodyQuery.data}
          setLastId={setLastId}
        />
      );
    }
  }, [bodyQuery.data]);
  useEffect(() => {
    if (lastId && accreditationVersions && document.getElementById(`accrediting_body_${lastId}`)) {
      setLastId(null); // Reset lastId after scrolling
      document.getElementById(`accrediting_body_${lastId}`)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastId, accreditationVersions]);

  const publishedStateOptions: { value: 'DRAFT' | 'PUBLISHED' | ''; label: string }[] = [
    {
      value: '',
      label: `${t('awb.accreditingBodyVersions.properties.publishedState')}: ${t('common.all')}`,
    },
    { value: 'PUBLISHED', label: t('awb.accreditingBodyVersions.publishedState.PUBLISHED') },
    { value: 'DRAFT', label: t('awb.accreditingBodyVersions.publishedState.DRAFT') },
  ];

  const gridViewQueryData = useMemo(
    () => ({
      hasNextPage: gridViewQuery.hasNextPage,
      isFetchingNextPage: gridViewQuery.isFetchingNextPage,
      fetchNextPage: gridViewQuery.fetchNextPage,
      isError: gridViewQuery.isError,
      isLoading: gridViewQuery.isLoading,
      error: gridViewQuery.error,
      data: {
        pages:
          gridViewQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [gridViewQuery]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('awb.accreditingBodyVersions.title', '', { bodyName: bodyQuery.data?.name || '' }),
        subtitle: bodyQuery.data?.abbreviation,
        titleBadge:
          (gridViewQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={gridViewQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
        actions: actions,
        breadcrumbItems: [
          <Link
            key='accrediting-bodies'
            href='/admin/awb/accrediting-bodies'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {t('awb.accreditingBodies.title')}
          </Link>,
          <Typography
            key='versions'
            color='text.primary'
          >
            {bodyQuery.data?.name || ''}
          </Typography>,
        ],
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 180px 180px 180px 1fr',
                gap: 3,
                flex: 1,
              }}
            >
              <QuickSearch
                id='keyword_search'
                initialValue={params.name || ''}
                onChange={(val) => updateParams({ name: val })}
                placeholder={t('awb.accreditingBodyVersions.search.placeholder')}
              />
              <JurisdictionPicker
                id='jurisdiction_filter'
                allowBlank
                value={!!params.jurisdiction ? [params.jurisdiction] : []}
                onChange={(val) => updateParams({ jurisdiction: val[0] })}
                placeholder={`${t('awb.accreditingBodyVersions.properties.jurisdiction')}: ${t('common.all')}`}
                size='small'
                sx={{ paddingTop: '0px !important' }}
              />
              <VerticalPicker
                id='vertical_filter'
                allowBlank
                value={!!params.vertical ? [params.vertical] : []}
                onChange={(val) => updateParams({ vertical: val[0] })}
                placeholder={`${t('awb.accreditingBodyVersions.properties.vertical')}: ${t('common.all')}`}
                size='small'
                sx={{ paddingTop: '0px !important' }}
              />
              <ReactSelect
                name='published_state_filter'
                value={publishedStateOptions.find((opt) => opt.value === params.publishedState)}
                placeholder={`${t('awb.accreditingBodyVersions.properties.publishedState')}: ${t('common.all')}`}
                options={publishedStateOptions}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, width: 300 }),
                  input: (base) => ({
                    ...base,
                    boxShadow: 'none',
                    outline: 0,
                    minHeight: 26,
                  }),
                }}
                menuPosition='fixed'
                menuPlacement='auto'
                onChange={(selOption) => updateParams({ publishedState: selOption?.value ?? '' })}
              />
              <Box />
            </Box>
          }
        />
        <GridSection
          columns={columns}
          data={accreditationVersions}
          query={gridViewQueryData}
        />
      </Box>

      {editId && (
        <EditAccreditingBodyVersion
          key={`edit_${editId}`}
          id={editId}
          accreditingBody={bodyQuery.data!}
          setLastId={setLastId}
          setEditId={setEditId}
        />
      )}

      {cloneId && (
        <CloneVersion
          key={`clone_${cloneId}`}
          sourceVersionId={cloneId}
          accreditingBody={bodyQuery.data!}
          setLastId={setLastId}
          setCloneId={setCloneId}
        />
      )}

      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t('awb.accreditingBodyVersions.release.modal.title')}
        subtitle={t('awb.accreditingBodyVersions.release.modal.subtitle')}
        size='sm'
        actions={
          <>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => setShowConfirmModal(false)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleConfirmRelease}
              disabled={isSaving}
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {t('awb.accreditingBodyVersions.release.modal.confirm')}
            </Button>
          </>
        }
      >
        <Stack spacing={3}>
          <Typography variant='body1'>{t('awb.accreditingBodyVersions.release.modal.description')}</Typography>

          <RadioGroup
            value={notificationMode}
            onChange={(e) => setNotificationMode(e.target.value as 'silent' | 'notify')}
          >
            <FormControlLabel
              value='silent'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.description')}
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value='notify'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.description')}
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>
        </Stack>
      </Modal>
    </CommonLayout>
  );
};

export default AccreditationVersionsList;
